import React from "react";
import { Link } from "react-router-dom";
import { FileText, ChevronRight, Lock } from "lucide-react";

/**
 * QuotePipelineWidget — custom renderer for widget_key = "quote_pipeline".
 *
 * The aggregator returns:
 *   payload.data = { count, buckets: { draft, sent, accepted, declined }, label }
 *
 * Wave 2A displays four bucket tiles (Draft / Sent / Accepted / Declined).
 * ToursList does not support quote-status query params (its `status` filter
 * is tour status, not quote status), so all bucket click-throughs
 * substitute to the existing `/reports/quote-pipeline` report route which
 * already shows per-status counts + USD totals. This substitution is
 * documented in the deliverable.
 */

const BUCKETS = [
  { key: "draft",     label: "Draft",     tone: "bg-chrome-mist text-carbon" },
  { key: "sent",      label: "Sent",      tone: "bg-coach-blue/10 text-coach-blue" },
  { key: "accepted",  label: "Accepted",  tone: "bg-touring-lime/20 text-carbon" },
  { key: "declined",  label: "Declined",  tone: "bg-critical-red/10 text-critical-red" },
];

const BUCKET_ROUTES = {
  draft:    "/reports/quote-pipeline",
  sent:     "/reports/quote-pipeline",
  accepted: "/reports/quote-pipeline",
  declined: "/reports/quote-pipeline",
};

const Restricted = () => (
  <div
    data-testid="widget-quote_pipeline"
    data-widget-tone="denied"
    className="bg-road-white/60 dark:bg-carbon/40 border border-dashed border-coach-silver dark:border-steel/40 rounded-md p-4 min-h-[148px] flex flex-col"
  >
    <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
      <Lock size={11} /> Quote Pipeline
    </div>
    <div className="mt-2 text-[13px] font-medium text-steel dark:text-coach-silver">
      Restricted
    </div>
  </div>
);

export default function QuotePipelineWidget({ payload }) {
  if (payload?.unavailable_reason === "permission_denied") return <Restricted />;
  const buckets = payload?.data?.buckets || {};
  const total = payload?.data?.count ?? Object.values(buckets).reduce((a, b) => a + (b || 0), 0);

  return (
    <div
      data-testid="widget-quote_pipeline"
      className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-4 min-h-[148px] flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-widest text-steel">
          <FileText size={11} /> Quote Pipeline
        </div>
        <div className="text-[10.5px] text-steel tabular-nums" data-testid="widget-quote_pipeline-total">
          {total} total
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 flex-1">
        {BUCKETS.map((b) => {
          const n = buckets[b.key] || 0;
          return (
            <Link
              key={b.key}
              to={BUCKET_ROUTES[b.key]}
              data-testid={`widget-quote_pipeline-${b.key}`}
              className={`group rounded-sm border border-transparent hover:border-coach-blue transition ${b.tone} px-2.5 py-2 flex items-baseline justify-between`}
            >
              <span className="text-[11px] uppercase tracking-wider">{b.label}</span>
              <span className="font-display text-lg tabular-nums leading-none">{n}</span>
            </Link>
          );
        })}
      </div>
      <Link
        to="/reports/quote-pipeline"
        data-testid="widget-quote_pipeline-view-all"
        className="mt-3 flex items-center justify-end gap-1 text-[11px] text-coach-blue hover:underline"
      >
        View Pipeline Report <ChevronRight size={12} />
      </Link>
    </div>
  );
}
